import { LoginInfo, ApiInterface } from '../types';
import {
  handleSuccess,
  handleError,
  getCookie,
  sleep,
  SPORTS_GET_USERINFO_API,
  getLoginInfo,
  report,
  getSportsLoginMiddlePage,
} from '../helper';
import JsApi from '@tencent/tvf-jsapi';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Base } from './base';

const clearSportsCookies = () => {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  cookies.forEach((cookie) => {
    const [key] = cookie.split('=');
    Cookies.remove(key, { domain: 'sports.qq.com' });
  });
};

/**
 * 腾讯体育
 */
export class SportsApi extends Base implements ApiInterface {
  public needRefresh = false; // 是否需要续期，外部可以根据此属性进行事件监听并在续期后处理逻辑
  public refreshed = false;
  public ifRefreshSuccess = false;

  /**
   * 是否在客户端内
   */
  private isInApp: boolean;

  public constructor({ isInApp }: { isInApp: boolean }) {
    super();
    this.isInApp = isInApp;
  }

  /**
   * 登录
   */
  @report('login')
  public async login() {
    // 端内
    if (this.isInApp) {
      try {
        const { errCode, errMsg } = await JsApi.invoke('actionLogin');
        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }

        // 需要手动刷新才能拿到cookie
        location.reload();
        return handleSuccess(true);
      } catch (error) {
        const { errCode } = error as any;
        if (errCode === 3003) {
          return handleError(-1, '当前版本过低，可能会导致登录失败，请升级至最新版本再试~');
        }
        // 如果取消登录会报错
        return handleError(-1, '取消登录');
      }
    }

    // 端外跳中间页
    window.location.href = getSportsLoginMiddlePage();

    // 如果业务侧代码有 location.reload()，那么等待两秒返回以保证 href 正确跳转
    await sleep(2000);

    // IOS 和部分安卓在 location.href 跳转页面后，此时如果没有登录而是直接回退页面时不会刷新页面
    // 因此状态码返回 -1 为了防止 JS 继续执行时误判登录成功
    return handleError(-1, '取消登录');
  }

  /**
   * 退出登录
   */
  @report('logout')
  public async logout(needReload = true) {
    // 端内
    if (this.isInApp) {
      return handleError(-1, '腾讯体育不支持 jsBridge 退出登录，需要在 app 中退出');
    }

    // 未登录不允许调用
    const { result: isLogin } = await this.isLogin();
    if (!isLogin) {
      return handleError(-1, '当前未登录，无法退出登录');
    }

    // 端外清空 cookie
    clearSportsCookies();
    if (needReload) {
      location.reload();
    }
    return handleSuccess(true);
  }

  /**
   * 是否登录
   */
  @report('isLogin')
  public async isLogin() {
    const openid = getCookie('vqq_openid') || getCookie('openid');

    return handleSuccess(!!openid);
  }

  /**
   * 获取登录信息，cookie
   */
  @report('getLoginInfo')
  public async getLoginInfo() {
    return getLoginInfo({
      isInApp: this.isInApp,
      loginInfoHandler: (_loginInfo: LoginInfo, appCookies: Record<string, string>) => {
        const loginInfo = _loginInfo;

        loginInfo.appid = getCookie('vqq_appid', appCookies) || getCookie('appid', appCookies);
        loginInfo.openid = getCookie('vqq_openid', appCookies) || getCookie('openid', appCookies);
        loginInfo.accessToken = getCookie('vqq_access_token', appCookies) || getCookie('access_token', appCookies);
        loginInfo.refreshToken = getCookie('vqq_refresh_token', appCookies) || getCookie('refresh_token', appCookies);
      },
    });
  }

  /**
   * 获取用户信息，头像昵称
   */
  @report('getUserInfo')
  public async getUserInfo() {
    const userInfo = {
      nickname: '',
      avatar: '',
    };

    // 端内
    if (this.isInApp) {
      try {
        const { errCode, errMsg, result } = await JsApi.invoke('getMainUserInfo');
        if (errCode !== 0) {
          return handleError(errCode, errMsg);
        }
        userInfo.nickname = result?.userInfo?.nickname || '';
        userInfo.avatar = result?.userInfo?.headImgUrl || '';
      } catch (error) {
        return handleError(-1, '未登录，无法获取用户信息');
      }
    } else {
      const { result: isLogin } = await this.isLogin();
      if (!isLogin) {
        return handleError(-1, '未登录，无法获取用户信息');
      }

      const res = await axios.get(SPORTS_GET_USERINFO_API, {
        withCredentials: true,
      });
      const [, info] = res.data;
      userInfo.avatar = info.icon;
      userInfo.nickname = info.nick;
    }

    return handleSuccess(userInfo);
  }
}
